import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authedFetch } from '../utils/shopifyFetch'
import { withoutOrders } from '../lib/orderSection'

// This is the menu Shopify draws beside the app in the admin, and it is a
// separate list from the one in main.jsx and the in-app sidebar. All three have
// to agree: Templates was taken out of the other two and stayed here, so it was
// still one click away from a section that no longer exists on its own.
//
// Dashboard is not listed either. It keeps its route — it is the app root and
// where the upload flow starts — but every template is now created inside the
// listing rule that uses it, so neither needs an entry.
//
// `hubOnly` hides an entry for a hub it does not apply to. A listing rule
// decides the category, price and policies a product is published to eBay
// under; with eBay as the hub nothing is published from here, so the whole
// section has nothing to decide. It is hidden, not removed — the routes still
// exist and still work for anyone who has a link to one.
const NAV_LINKS = [
  { href: '/products',   label: 'Products'        },
  // Dropped by withoutOrders below for an eBay-hub shop, and kept for the
  // shopify and two_way shops that still have the section. Left in the list
  // rather than deleted, so the two navs cannot drift apart.
  { href: '/orders',     label: 'Orders'          },
  { href: '/profiling',  label: 'Listing Rules',  hubOnly: 'shopify' },
  { href: '/activity',   label: 'Activity Log'    },
  { href: '/pricing',    label: 'Pricing',        hideable: 'pricing' },
  { href: '/settings',   label: 'Settings'        },
]

const isEmbedded = (() => { try { return window.self !== window.top } catch { return true } })()

export default function ShopifyNavMenu({ shop }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [hub, setHub] = useState(null)
  // The custom-app shop keeps Orders whatever its hub — see lib/orderSection.
  const [customApp, setCustomApp] = useState(false)
  // Hidden for one shop by a per-shop setting; see migration 067.
  const [hidePricing, setHidePricing] = useState(false)

  // Read once. Until it answers, the menu renders as it always did — a nav
  // that flickers an entry away a moment after it draws is worse than one that
  // settles a moment late.
  useEffect(() => {
    if (!shop) return
    let alive = true
    authedFetch(`/api/settings?shop=${encodeURIComponent(shop)}`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return
        setHub(d?.sync?.syncSource || 'two_way')
        setCustomApp(d?.shopify?.customApp === true)
        setHidePricing(d?.sync?.hidePricing === true)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [shop])

  // Redirect root to /dashboard so the Dashboard nav item is highlighted
  useEffect(() => {
    if (location.pathname === '/') navigate('/dashboard', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isEmbedded) return null

  return (
    <s-app-nav>
      {/* Hidden home route — sets default landing page without showing in menu */}
      <s-link href="/" rel="home">Home</s-link>
      {/* Orders for every hub but eBay. `hub` is null until settings answer,
          which shows the entry — a menu that settles a moment late is better
          than one that takes an entry away in front of whoever is looking. */}
      {withoutOrders(NAV_LINKS, hub, customApp)
        .filter(({ hubOnly }) => !(hubOnly === 'shopify' && hub && hub !== 'shopify'))
        .filter(({ hideable }) => !(hideable === 'pricing' && hidePricing))
        .map(({ href, label }) => (
          <s-link key={href} href={href}>{label}</s-link>
        ))}
    </s-app-nav>
  )
}
